import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import config from "../config.js";

/**
 * useCurrentTripStartStopLogs
 *
 * Polls GET /location-data/current-trip/:deviceId?organizationId=xxx
 * and returns ONLY the start/stop records that belong to the trip
 * currently in progress (everything after the last completed trip).
 *
 * @param {string}  vehicleId       - device_id of the vehicle
 * @param {number}  organizationId  - org ID
 * @param {number}  pollInterval    - ms between polls (default 30 000)
 * @param {boolean} enabled         - set false to pause polling
 */
const useCurrentTripStartStopLogs = (
  vehicleId,
  organizationId,
  pollInterval = 30000,
  enabled = true,
) => {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [tripStatus, setTripStatus] = useState(null); // "IN_PROGRESS" | "COMPLETED" | "NO_DATA" | "PENDING_ORIGIN"
  const [routeInfo, setRouteInfo]   = useState(null);
  const [lastFetch, setLastFetch]   = useState(null);

  const intervalRef    = useRef(null);
  const isMountedRef   = useRef(true);
  const isFetchingRef  = useRef(false);   // prevent overlapping requests

  const fetchLogs = useCallback(async () => {
    if (!vehicleId || !organizationId || !enabled) return;
    if (isFetchingRef.current) return; // skip if previous call still in-flight

    isFetchingRef.current = true;
    if (logs.length === 0) setLoading(true); // show spinner only on first load

    try {
      const response = await axios.get(
        `${config.baseURL}/location/current-trip/${vehicleId}`,
        {
          params: { organizationId },
          withCredentials: true,
        },
      );

      if (!isMountedRef.current) return;

      const data = response?.data?.data;
      if (data) {
        setLogs(data.startStopRecords ?? []);
        setTripStatus(data.tripStatus ?? null);
        setRouteInfo(data.routeInfo ?? null);
        setError(null);
        setLastFetch(new Date());

        // If the trip just completed, log it so callers can react
        if (data.tripStatus === "COMPLETED") {
          console.log(
            "✅ [useCurrentTripStartStopLogs] Trip COMPLETED:",
            data.completionInfo,
          );
        }
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error("[useCurrentTripStartStopLogs] fetch error:", err);
      setError(err.message ?? "Unknown error");
    } finally {
      if (isMountedRef.current) setLoading(false);
      isFetchingRef.current = false;
    }
  }, [vehicleId, organizationId, enabled]); // logs intentionally omitted – no need to re-create on every logs change

  // ── Mount / unmount ─────────────────────────────────────────────────────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // ── Polling ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !vehicleId || !organizationId) {
      clearInterval(intervalRef.current);
      return;
    }

    // Immediate first fetch
    fetchLogs();

    intervalRef.current = setInterval(fetchLogs, pollInterval);

    return () => clearInterval(intervalRef.current);
  }, [vehicleId, organizationId, pollInterval, enabled, fetchLogs]);

  return { logs, loading, error, tripStatus, routeInfo, lastFetch, refetch: fetchLogs };
};

export default useCurrentTripStartStopLogs;